/**
 * Viridis Color Scale
 *
 * Returns a color from the Viridis colormap based on a normalized value (0-100).
 * Viridis is a perceptually uniform colormap that ranges from yellow (low) to purple (high).
 *
 * @param {number} value - Normalized value between 0 and 100
 * @param {number} [alpha=1] - Optional alpha/transparency value (0-1)
 * @returns {string} RGBA color string, e.g., "rgba(253, 231, 37, 1)"
 *
 * @example
 * viridis(0)    // "rgba(253, 231, 37, 1)"  - bright yellow
 * viridis(50)   // "rgba(33, 145, 140, 1)"  - teal
 * viridis(100)  // "rgba(68, 1, 84, 1)"     - dark purple
 * viridis(75, 0.5) // "rgba(72, 29, 111, 0.5)" - semi-transparent purple
 */
export function viridis(value, alpha = 1) {
  // Clamp value to 0-100 range
  const normalized = Math.max(0, Math.min(100, value)) / 100;

  // Viridis colormap data points (REVERSED - yellow to purple)
  // Format: [r, g, b] for each step
  const viridisColors = [
    [253, 231, 37], // 0% - bright yellow
    [241, 250, 19],
    [215, 248, 13],
    [189, 246, 14],
    [162, 243, 22],
    [139, 240, 35], // 20%
    [117, 235, 50],
    [99, 230, 63],
    [82, 223, 76],
    [68, 217, 87],
    [54, 208, 100], // 40%
    [42, 197, 113],
    [37, 189, 120],
    [35, 179, 127],
    [36, 165, 134],
    [38, 151, 139], // 60%
    [41, 137, 142],
    [45, 122, 142],
    [49, 109, 142],
    [53, 96, 141],
    [58, 83, 139], // 80%
    [63, 69, 135],
    [68, 56, 130],
    [71, 42, 122],
    [72, 29, 111],
    [71, 16, 99],
    [68, 1, 84], // 100% - dark purple
  ];

  // Calculate which color indices to interpolate between
  const scaledValue = normalized * (viridisColors.length - 1);
  const lowerIndex = Math.floor(scaledValue);
  const upperIndex = Math.ceil(scaledValue);
  const fraction = scaledValue - lowerIndex;

  // Get the two colors to interpolate between
  const lowerColor = viridisColors[lowerIndex];
  const upperColor = viridisColors[upperIndex];

  // Linear interpolation between the two colors
  const r = Math.round(
    lowerColor[0] + (upperColor[0] - lowerColor[0]) * fraction
  );
  const g = Math.round(
    lowerColor[1] + (upperColor[1] - lowerColor[1]) * fraction
  );
  const b = Math.round(
    lowerColor[2] + (upperColor[2] - lowerColor[2]) * fraction
  );

  // Clamp alpha to 0-1 range
  const clampedAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
}

/**
 * Get a hex color from the Viridis color scale
 *
 * @param {number} value - Normalized value between 0 and 100
 * @returns {string} Hex color string, e.g., "#fde725"
 *
 * @example
 * viridisHex(0)   // "#fde725" - yellow
 * viridisHex(50)  // "#21918c" - teal
 * viridisHex(100) // "#440154" - purple
 */
export function viridisHex(value) {
  const rgba = viridis(value);
  // Extract RGB values from rgba string
  const matches = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!matches) return "#000000";

  const r = parseInt(matches[1]);
  const g = parseInt(matches[2]);
  const b = parseInt(matches[3]);

  // Convert to hex
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Get RGB object from the Viridis color scale
 *
 * @param {number} value - Normalized value between 0 and 100
 * @returns {{r: number, g: number, b: number}} RGB object
 *
 * @example
 * viridisRGB(50) // { r: 33, g: 145, b: 140 }
 */
export function viridisRGB(value) {
  const rgba = viridis(value);
  const matches = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!matches) return { r: 0, g: 0, b: 0 };

  return {
    r: parseInt(matches[1]),
    g: parseInt(matches[2]),
    b: parseInt(matches[3]),
  };
}

export default viridis;
